import {
    useInfiniteQuery,
    UseInfiniteQueryResult,
    useMutation,
    UseMutationResult,
    useQuery,
    useQueryClient,
    UseQueryResult,
} from '@tanstack/react-query';
import axios, {AxiosError} from 'axios';
import {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {type TypedTranslation} from '../../../i18n';
import useDebounceSearchTerm from '../../../hooks/useDebounceSearchTerm';
import {IPresentation} from '../../../incase-domain/presentation';
import {ICreatedRoom, ICreateRoom, IFetchRoom, IUpdateRoom} from '../../../incase-domain/rooms/room.editor';
import {IPaginatedResult, SORT} from '../../../incase-domain/search';
import {User} from '../../../incase-domain/user';
import {UserFilterOptions} from '../../../incase-domain/userFilterOptions';
import {fetchUserPage} from '../../../service/authorization';
import {fetchPresentationListPaginated} from '../../../service/presentation';
import {
    bulkDeleteRoomService,
    createRoomService,
    deleteRoomService,
    duplicateRoomsService,
    fetchRoomService,
    fetchRoomsListPaginated,
    resendInvitationService,
    updateRoomService,
} from '../../../service/room.service';
import {QUERY_KEYS} from '../../../utils/queryKeys';
import {getNextPage} from '../../../utils/pagination';
import {IApiError} from '../../../incase-domain/error';

const FIRST_PAGE = 1;
const PAGE_SIZE = 20;

interface IFetchRoomsPaginatedProps {
    pageNumber: number;
    pageSize: number;
    searchTerm: string;
    sortBy?: Record<string, SORT>;
}

interface IFetchPresenterProps {
    pageNumber: number;
    pageSize: number;
    filterQuery: UserFilterOptions;
    searchTerm?: string;
    sortBy?: Record<string, SORT>;
    enabled?: boolean;
}

interface IFetchPresentationProps {
    pageNumber: number;
    pageSize: number;
    searchTerm?: string;
    sortBy?: Record<string, SORT>;
    includeSharedOnes?: boolean;
    enabled?: boolean;
}

interface IFetchRoomProps {
    roomId: string;
    onSuccess?: (data: IFetchRoom) => void;
}

interface IUseRemoveRoomProps {
    onDeletePageChangeHandler?: (deletedCount?: number) => void;
}

type IUseBulkRemoveRoomsProps = IUseRemoveRoomProps;

export const useFetchRooms = ({
    pageNumber,
    pageSize,
    searchTerm,
    sortBy,
}: IFetchRoomsPaginatedProps): UseQueryResult<IPaginatedResult<IFetchRoom>> => {
    return useQuery({
        queryKey: QUERY_KEYS.room.list({pageNumber, pageSize, searchTerm, sortBy}),
        queryFn: async () => fetchRoomsListPaginated(pageNumber, pageSize, searchTerm, sortBy),
        keepPreviousData: true,
        refetchInterval: 10000,
    });
};

export const useRemoveRoom = ({onDeletePageChangeHandler}: IUseRemoveRoomProps = {}): UseMutationResult<
    {success: boolean},
    unknown,
    string
> => {
    const queryClient = useQueryClient();
    const {t} = useTranslation();

    return useMutation((roomId: string) => deleteRoomService(roomId), {
        onSuccess: async (_, roomId) => {
            await Promise.all([
                queryClient.invalidateQueries({queryKey: QUERY_KEYS.room.lists()}),
                queryClient.invalidateQueries({queryKey: QUERY_KEYS.room.detail(roomId)}),
            ]);
            onDeletePageChangeHandler && onDeletePageChangeHandler();
            toast.success(t('roomsPage.roomDeleted'));
        },
    });
};

export const useBulkRemoveRooms = ({onDeletePageChangeHandler}: IUseBulkRemoveRoomsProps = {}): UseMutationResult<
    {success: boolean},
    unknown,
    string[]
> => {
    const queryClient = useQueryClient();
    const {t} = useTranslation();

    return useMutation((arrOfRoomsId: string[]) => bulkDeleteRoomService(arrOfRoomsId), {
        onSuccess: async () => {
            await queryClient.invalidateQueries({queryKey: QUERY_KEYS.room.lists()});
            onDeletePageChangeHandler && onDeletePageChangeHandler();
            toast.success(t('roomsPage.roomsDeleted'));
        },
    });
};

export const useFetchPresenters = ({
    searchTerm,
    filterQuery,
    sortBy,
}: Omit<IFetchPresenterProps, 'pageNumber' | 'pageSize'>): UseInfiniteQueryResult<IPaginatedResult<User>> => {
    const debouncedSearchTerm = useDebounceSearchTerm(searchTerm, 500);

    const fetchPage = ({pageParam = FIRST_PAGE}): Promise<IPaginatedResult<User>> =>
        fetchUserPage(pageParam, PAGE_SIZE, filterQuery, debouncedSearchTerm, sortBy);

    return useInfiniteQuery({
        queryKey: QUERY_KEYS.room.sub('presenterPicker', debouncedSearchTerm, filterQuery, sortBy),
        queryFn: fetchPage,
        getNextPageParam: getNextPage,
    });
};

export const useFetchPresentations = ({
    searchTerm,
    sortBy,
    includeSharedOnes = true,
    enabled,
}: Omit<IFetchPresentationProps, 'pageNumber' | 'pageSize'>): UseInfiniteQueryResult<IPaginatedResult<IPresentation>> => {
    const debouncedSearchTerm = useDebounceSearchTerm(searchTerm, 500);

    const fetchPage = ({pageParam = FIRST_PAGE}): Promise<IPaginatedResult<IPresentation>> =>
        fetchPresentationListPaginated(pageParam, PAGE_SIZE, debouncedSearchTerm, sortBy, includeSharedOnes);

    return useInfiniteQuery({
        queryKey: QUERY_KEYS.room.sub('presentationPicker', debouncedSearchTerm, sortBy, includeSharedOnes),
        queryFn: fetchPage,
        getNextPageParam: getNextPage,
        enabled,
    });
};

export const useCreateRoom = (): UseMutationResult<ICreatedRoom, IApiError, ICreateRoom> => {
    const queryClient = useQueryClient();
    const {t} = useTranslation();

    return useMutation((variables: ICreateRoom) => createRoomService(variables), {
        onSuccess: () => {
            queryClient
                .invalidateQueries({
                    queryKey: QUERY_KEYS.room.lists(),
                })
                .then(() => {
                    setTimeout(() => {
                        toast.success(t('roomsPage.roomCreated'));
                    }, 100);
                });
        },
        onError: (error) => {
            if (axios.isAxiosError(error)) {
                const message = error.response?.data?.message;

                toast.error(t(message) ?? t('roomsPage.roomCreationFailed'));
                return;
            }

            toast.error(t('roomsPage.roomCreationFailed'));
        },
    });
};

export const useFetchRoom = ({roomId, onSuccess}: IFetchRoomProps): UseQueryResult<IFetchRoom> => {
    const queryClient = useQueryClient();

    useEffect(() => {
        queryClient.invalidateQueries({queryKey: QUERY_KEYS.room.detail(roomId)});
    }, [roomId, queryClient]);

    return useQuery({
        queryKey: QUERY_KEYS.room.detail(roomId),
        queryFn: () => fetchRoomService(roomId),
        enabled: !!roomId,
        onSuccess: (room) => {
            if (onSuccess && room) {
                onSuccess(room);
            }
        },
    });
};

export const useUpdateRoom = (onSuccess?: () => void): UseMutationResult<IUpdateRoom, unknown, IUpdateRoom> => {
    const queryClient = useQueryClient();
    const {t} = useTranslation();

    return useMutation((room: IUpdateRoom) => updateRoomService(room), {
        onSuccess: (updatedRoom) => {
            Promise.all([
                queryClient.invalidateQueries({queryKey: QUERY_KEYS.room.lists()}),
                queryClient.invalidateQueries({queryKey: QUERY_KEYS.room.detail(updatedRoom._id)}),
            ]).then(() => {
                toast.success(t('roomsPage.roomEdited'));
                onSuccess?.();
            });
        },
        onError: (error: AxiosError) => {
            if (error.response && error.response.status === 400) {
                toast.error(t(error.response.data as TypedTranslation));
            } else {
                toast.error(t('roomsPage.roomEditError'));
            }
        },
    });
};

export const useDuplicateRooms = (
    onSuccess?: (createdRooms: ICreatedRoom[]) => void
): UseMutationResult<ICreatedRoom[], unknown, string[]> => {
    const queryClient = useQueryClient();
    const {t} = useTranslation();

    return useMutation((roomIds: string[]) => duplicateRoomsService(roomIds), {
        onSuccess: async (createdRooms) => {
            await queryClient.invalidateQueries({queryKey: QUERY_KEYS.room.lists()});

            if (onSuccess) {
                onSuccess(createdRooms);
            }

            toast.success(t('roomsPage.roomsDuplicated'));
        },
        onError: (error: AxiosError<{message?: string}>) => {
            const message = error.response?.data?.message;

            if (error.response?.status === 404) {
                toast.error(t('roomsPage.roomsNotFound'));
            } else if (message === 'roomsPage.maxRoomsReached') {
                toast.error(t('roomsPage.maxRoomsReached'));
            } else {
                toast.error(t('roomsPage.roomsDuplicationError'));
            }
        },
    });
};

export const useResendInvitation = (): UseMutationResult<{outboxMessageId: string}, unknown, string> => {
    return useMutation({
        mutationFn: (roomId: string) => resendInvitationService(roomId),
    });
};
